/**
 * QuestPanel — Shows quest progress on the Overview page.
 *
 * Displays active & claimable quests prominently, completed quests
 * in a collapsed section. Claimable quests get a "Claim" button
 * that triggers a toast and saves completion to state.
 *
 * Self-contained — reads all data from useApp() and the quest engine.
 */
import { useState, useMemo, useCallback } from "react";
import { Card } from '../../components/Card';
import { Heading } from '../../components/Heading';
import { useApp } from '../../hooks/useApp';
import { evaluateQuests, questStats, claimQuest } from './questEngine';

/* ─── Category colors ─── */
const CAT_COLORS = {
  safety: "#FF9F0A",
  credit: "#BF5AF2",
  budget: "#30D158",
  invest: "#0A84FF",
  streak: "#FF375F",
};

export default function QuestPanel() {
  const { s, c, proj, up, showToast } = useApp();
  const [showCompleted, setShowCompleted] = useState(false);
  const [justClaimed, setJustClaimed] = useState(null);

  const evaluated = useMemo(() => evaluateQuests(s, proj), [s, proj]);
  const stats = useMemo(() => questStats(evaluated), [evaluated]);

  const handleClaim = useCallback((quest) => {
    const updated = claimQuest(s.questsCompleted, quest.id);
    up("questsCompleted", updated);
    setJustClaimed(quest.id);
    showToast(quest.reward, "success");
    // Clear the claim animation after a moment
    setTimeout(() => setJustClaimed(null), 2000);
  }, [s.questsCompleted, up, showToast]);

  const active = evaluated.filter((q) => q.status === "active" || q.status === "claimable");
  const completed = evaluated.filter((q) => q.status === "completed");
  const locked = evaluated.filter((q) => q.status === "locked");

  return (
    <Card c={c} delay={0.08}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 20, flexWrap: "wrap", gap: 8,
      }}>
        <Heading ic={null} c={c}>
          <span style={{ fontSize: 17 }}>🗺️</span> Financial Quests
        </Heading>
        <div style={{
          padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
          background: stats.claimable > 0
            ? (s.dark ? "rgba(48,209,88,0.12)" : "rgba(48,209,88,0.06)")
            : (s.dark ? "rgba(10,132,255,0.10)" : "rgba(10,132,255,0.06)"),
          color: stats.claimable > 0 ? c.green : c.blue,
        }}>
          {stats.claimable > 0
            ? `${stats.claimable} ready to claim!`
            : `${stats.completed}/${stats.total} complete`
          }
        </div>
      </div>

      {/* Active + Claimable quests */}
      {active.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {active.map((quest) => (
            <QuestRow
              key={quest.id}
              quest={quest}
              c={c}
              s={s}
              onClaim={handleClaim}
              justClaimed={justClaimed === quest.id}
            />
          ))}
        </div>
      ) : locked.length > 0 && completed.length < evaluated.length ? (
        <div style={{ fontSize: 13, color: c.muted, padding: "8px 0" }}>
          Complete earlier quests to unlock new ones.
        </div>
      ) : null}

      {/* All complete message */}
      {active.length === 0 && locked.length === 0 && completed.length === evaluated.length && (
        <div style={{
          textAlign: "center", padding: "16px 0",
          fontSize: 14, fontWeight: 600, color: c.green,
        }}>
          All quests complete. You're building something real. 💎
        </div>
      )}

      {/* Locked quests (show count) */}
      {locked.length > 0 && (
        <div style={{
          fontSize: 12, color: c.muted, marginTop: 16, opacity: 0.6,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          🔒 {locked.length} quest{locked.length > 1 ? "s" : ""} locked — complete prerequisites to unlock
        </div>
      )}

      {/* Completed quests (collapsible) */}
      {completed.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, color: c.muted,
              padding: "4px 0", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <span style={{
              display: "inline-block",
              transition: "transform 0.2s ease",
              transform: showCompleted ? "rotate(90deg)" : "rotate(0deg)",
            }}>
              ▸
            </span>
            {completed.length} completed
          </button>
          {showCompleted && (
            <div style={{
              marginTop: 8, display: "flex", flexDirection: "column", gap: 8,
              animation: "fadeSlideUp 0.3s ease",
            }}>
              {completed.map((quest) => (
                <div key={quest.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 12px", borderRadius: 10,
                  background: s.dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                  opacity: 0.6,
                }}>
                  <span style={{ fontSize: 16, filter: "grayscale(0.3)" }}>{quest.icon}</span>
                  <span style={{ fontSize: 13, color: c.muted, textDecoration: "line-through", flex: 1 }}>
                    {quest.title}
                  </span>
                  <span style={{ fontSize: 11, color: c.green, fontWeight: 600 }}>✓</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   QUEST ROW — individual quest display
   ═══════════════════════════════════════════════════ */

function QuestRow({ quest, c, s, onClaim, justClaimed }) {
  const isClaimable = quest.status === "claimable";
  const catColor = CAT_COLORS[quest.category] || c.blue;
  const pct = Math.round(quest.progressValue * 100);

  return (
    <div style={{
      padding: "14px 16px", borderRadius: 14,
      background: isClaimable
        ? (s.dark ? "rgba(48,209,88,0.06)" : "rgba(48,209,88,0.03)")
        : (s.dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
      border: `1px solid ${isClaimable
        ? (s.dark ? "rgba(48,209,88,0.15)" : "rgba(48,209,88,0.12)")
        : "transparent"
      }`,
      transition: "all 0.3s ease",
      ...(justClaimed ? {
        animation: "fadeSlideUp 0.4s ease",
        background: s.dark ? "rgba(48,209,88,0.10)" : "rgba(48,209,88,0.06)",
      } : {}),
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        marginBottom: isClaimable ? 0 : 10,
      }}>
        {/* Icon */}
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: s.dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, flexShrink: 0,
        }}>
          {quest.icon}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            marginBottom: 2,
          }}>
            <span style={{
              fontSize: 14, fontWeight: 600, color: c.fg,
              letterSpacing: "-0.01em",
            }}>
              {quest.title}
            </span>
            <span style={{
              fontSize: 9, fontWeight: 700, color: catColor,
              letterSpacing: "0.08em", textTransform: "uppercase",
              padding: "2px 6px", borderRadius: 4,
              background: `${catColor}15`,
            }}>
              {quest.category}
            </span>
          </div>
          <div style={{ fontSize: 12, color: c.muted, lineHeight: 1.4 }}>
            {quest.desc}
          </div>
        </div>

        {/* Claim button or percentage */}
        {isClaimable ? (
          <button
            onClick={() => onClaim(quest)}
            style={{
              padding: "8px 18px", borderRadius: 20, border: "none",
              background: c.green, color: "#fff",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              flexShrink: 0, letterSpacing: "-0.01em",
              boxShadow: `0 2px 10px ${c.green}40`,
              transition: "all 0.2s cubic-bezier(0.25,0.1,0.25,1)",
            }}
          >
            Claim
          </button>
        ) : (
          <span style={{
            fontSize: 13, fontWeight: 700, color: catColor,
            fontVariantNumeric: "tabular-nums", flexShrink: 0,
          }}>
            {pct}%
          </span>
        )}
      </div>

      {/* Progress bar (active quests only) */}
      {!isClaimable && (
        <div style={{ marginTop: 2 }}>
          <div style={{
            height: 4, borderRadius: 2,
            background: s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 2,
              background: catColor,
              width: `${pct}%`,
              transition: "width 0.6s cubic-bezier(0.25,0.1,0.25,1)",
            }} />
          </div>
        </div>
      )}
    </div>
  );
}
